/**
 * First-touch acquisition attribution.
 *
 * Captures UTM params + document.referrer the FIRST time a visitor lands
 * on Fantastic Forms within a session, then persists them in
 * sessionStorage (and a long-lived localStorage fallback) so they survive
 * the visitor browsing the marketing site before signing up. Signup.js
 * reads `getAttribution()` at submit time and forwards them to the
 * backend, which stamps them on the new `users` row.
 *
 * Design notes:
 *  - First-touch wins. Subsequent visits with new UTM params do NOT
 *    overwrite the original attribution — this matches the convention
 *    used by GA4 / Plausible / most product analytics tools.
 *  - We DON'T persist if the URL has no UTM params AND the referrer is
 *    our own origin (or empty) — that's just an in-app nav, not a
 *    fresh acquisition touch.
 *  - localStorage holds the value for 30 days (TTL stamp). sessionStorage
 *    holds it for the current tab. We prefer sessionStorage for
 *    correctness; localStorage is the safety net for visitors who close
 *    the tab and return tomorrow.
 */

const SESSION_KEY = 'ff_attr';
const LOCAL_KEY = 'ff_attr_persist';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isOwnReferrer(ref) {
  if (!ref) return true;
  try {
    return new URL(ref).host === window.location.host;
  } catch {
    return false;
  }
}

function readFromUrl() {
  const p = new URLSearchParams(window.location.search);
  const utm = {
    utm_source:   p.get('utm_source')   || null,
    utm_medium:   p.get('utm_medium')   || null,
    utm_campaign: p.get('utm_campaign') || null,
    utm_term:     p.get('utm_term')     || null,
    utm_content:  p.get('utm_content')  || null,
  };
  const hasUtm = Object.values(utm).some(Boolean);
  const referrer = (!isOwnReferrer(document.referrer)) ? document.referrer : null;
  if (!hasUtm && !referrer) return null;
  return { ...utm, referrer };
}

/**
 * Call this on every route change. Cheap no-op when attribution is
 * already captured.
 */
export function captureAttributionIfFirstTouch() {
  try {
    // Already have it for this session — done.
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const fresh = readFromUrl();
    if (!fresh) return;
    const payload = { ...fresh, at: Date.now() };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
    localStorage.setItem(LOCAL_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage can throw in private-mode Safari pre-2020.
    // Silently degrade — Signup will just send nulls.
  }
}

/**
 * Read whatever attribution we have. Prefers the current session, falls
 * back to a fresh-enough localStorage entry. Returns {} when nothing.
 */
export function getAttribution() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
              || localStorage.getItem(LOCAL_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed?.at && Date.now() - parsed.at > TTL_MS) return {};
    const { at: _at, ...rest } = parsed || {};
    return rest;
  } catch {
    return {};
  }
}
